/**
 * 학원 디렉토리 지도 — 카카오맵 위에 학원 핀 표시
 * 핀 클릭 → 학원 상세, 호버 → 학원명 오버레이
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import academyService from '../../services/academyService';
import { useAuth } from '../../context/AuthContext';
import { useAcademy } from '../../context/AcademyContext';

const AcademyMapPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const { mode } = useAcademy();
  const mapRef = useRef(null);
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(true);

  const isDemo = userData?.provider === 'demo';

  useEffect(() => {
    let alive = true;
    (async () => {
      const list = await academyService.getApprovedAcademies();
      if (!alive) return;
      const filtered = isDemo && mode === 'personal'
        ? list.filter((a) => a.name === '팩토수학')
        : list;
      setAcademies(filtered);
      setLoading(false);
    })();
    return () => { alive = false; };
  }, [isDemo, mode]);

  useEffect(() => {
    if (loading) return undefined;
    let cancelled = false;

    const initMap = () => {
      const kakao = window.kakao;
      if (cancelled || !kakao?.maps || !mapRef.current) return;
      kakao.maps.load(() => {
        if (cancelled || !mapRef.current) return;
        const map = new kakao.maps.Map(mapRef.current, {
          center: new kakao.maps.LatLng(37.5665, 126.978),
          level: 6,
        });

        if (academies.length === 0) return;

        const geocoder = new kakao.maps.services.Geocoder();
        const bounds = new kakao.maps.LatLngBounds();
        let added = 0;

        academies.forEach((a) => {
          if (!a.location) return;
          geocoder.addressSearch(a.location, (result, status) => {
            if (cancelled || !mapRef.current) return;
            if (status !== kakao.maps.services.Status.OK || !result[0]) return;
            const pos = new kakao.maps.LatLng(result[0].y, result[0].x);
            bounds.extend(pos);
            added += 1;
            if (added === 1) map.setCenter(pos);
            if (added > 1) map.setBounds(bounds);

            // 점(dot) + 학원명 라벨이 같이 보이는 커스텀 오버레이
            const el = document.createElement('div');
            el.style.cssText = 'display:flex;align-items:center;gap:6px;cursor:pointer;transform:translateX(-7px);';
            el.innerHTML = `
              <span style="width:14px;height:14px;border-radius:50%;background:#4F46E5;border:2.5px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.25);flex-shrink:0;"></span>
              <span style="background:#fff;border:1px solid #E5E7EB;border-radius:7px;padding:4px 9px;font-size:12.5px;font-weight:700;color:#1A1A2E;box-shadow:0 2px 8px rgba(0,0,0,0.08);white-space:nowrap;">${a.name}</span>
            `;
            el.addEventListener('click', () => {
              navigate(`/app/academy/${a.id}`, { state: { subject: a.subject } });
            });

            const overlay = new kakao.maps.CustomOverlay({
              position: pos,
              content: el,
              xAnchor: 0,
              yAnchor: 0.5,
            });
            overlay.setMap(map);
          });
        });
      });
    };

    if (window.kakao?.maps) {
      initMap();
    } else {
      let tries = 0;
      const timer = setInterval(() => {
        tries += 1;
        if (window.kakao?.maps) {
          clearInterval(timer);
          initMap();
        } else if (tries > 50) {
          clearInterval(timer);
        }
      }, 200);
      return () => { cancelled = true; clearInterval(timer); };
    }
    return () => { cancelled = true; };
  }, [academies, loading, navigate]);

  return (
    <Wrap $demo={isDemo}>
      <Map ref={mapRef} />
      {!loading && (
        <Hint>학원 핀을 눌러 자세히 보기 · 총 {academies.length}개</Hint>
      )}
    </Wrap>
  );
};

export default AcademyMapPage;

const Wrap = styled.div`
  position: relative;
  height: ${({ $demo }) => ($demo ? 'calc(100vh - 56px - 64px - 38px)' : 'calc(100vh - 56px - 64px)')};
  margin-bottom: -72px;
  background: #EDEFF3;
`;

const Map = styled.div`
  width: 100%;
  height: 100%;
`;

const Hint = styled.div`
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 14px;
  border-radius: 99px;
  background: rgba(255, 255, 255, 0.95);
  font-size: 12.5px;
  font-weight: 700;
  color: #1A1A2E;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  white-space: nowrap;
`;
